import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { SavedPropertiesContext } from './SavedPropertiesContext.js'
import { track } from '../services/mixpanel.js'
import { trackEvent } from '../utils/trackEvent.js'

const STORAGE_KEY = 'hh:savedProperties'
const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? ''
const API_URL = `${API_BASE}/.netlify/functions/saved-properties`

const readFromStorage = () => {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const writeToStorage = (ids) => {
  if (typeof window === 'undefined') return
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids)) } catch { /* ignore */ }
}

const clearStorage = () => {
  if (typeof window === 'undefined') return
  try { window.localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
}

export const SavedPropertiesProvider = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0()
  const [savedIds, setSavedIds] = useState(() => readFromStorage())
  const [dbSynced, setDbSynced] = useState(false)
  const syncedSubRef = useRef(null)

  const getToken = useCallback(async () => {
    try { return await getAccessTokenSilently() } catch { return null }
  }, [getAccessTokenSilently])

  // On login: merge localStorage → DB, then use DB as source of truth
  useEffect(() => {
    if (!isAuthenticated || dbSynced) return
    let cancelled = false

    const sync = async () => {
      const token = await getToken()
      if (!token || cancelled) return

      const localIds = readFromStorage()
      try {
        // Merge local IDs into DB, get back full server list
        const res = await fetch(`${API_URL}/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ids: localIds }),
        })
        if (!res.ok) return
        const { ids } = await res.json()
        if (cancelled) return
        setSavedIds(ids)
        clearStorage()
        setDbSynced(true)
        syncedSubRef.current = token
      } catch { /* fall back to local */ }
    }

    sync()
    return () => { cancelled = true }
  }, [isAuthenticated, dbSynced, getToken])

  // On logout: reload from localStorage
  useEffect(() => {
    if (!isAuthenticated && dbSynced) {
      setSavedIds(readFromStorage())
      setDbSynced(false)
    }
  }, [isAuthenticated, dbSynced])

  // Persist locally when not using DB
  useEffect(() => {
    if (!dbSynced) writeToStorage(savedIds)
  }, [savedIds, dbSynced])

  const save = useCallback(async (id) => {
    setSavedIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
    track('property_saved', { listing_id: id })
    const token = await getToken()
    trackEvent('save', { propertyId: id, token })
    if (!dbSynced || !token) return
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ propertyId: id }),
      })
      if (res.ok) {
        const { ids } = await res.json()
        setSavedIds(ids)
      }
    } catch { /* optimistic update already applied */ }
  }, [dbSynced, getToken])

  const remove = useCallback(async (id) => {
    setSavedIds((prev) => prev.filter((s) => s !== id))
    if (!dbSynced) return
    const token = await getToken()
    if (!token) return
    try {
      const res = await fetch(`${API_URL}?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        const { ids } = await res.json()
        setSavedIds(ids)
      }
    } catch { /* optimistic update already applied */ }
  }, [dbSynced, getToken])

  const toggleSaved = useCallback((id) => {
    setSavedIds((prev) => {
      if (prev.includes(id)) { remove(id); return prev.filter((s) => s !== id) }
      save(id); return [...prev, id]
    })
  }, [save, remove])

  const isSaved = useCallback((id) => savedIds.includes(id), [savedIds])

  const value = useMemo(
    () => ({ savedIds, isSaved, toggleSaved, save, remove }),
    [savedIds, isSaved, toggleSaved, save, remove],
  )

  return <SavedPropertiesContext.Provider value={value}>{children}</SavedPropertiesContext.Provider>
}
